import { useState } from "react";
import { createFileRoute, Link, Outlet, useLocation, getRouteApi } from "@tanstack/react-router";
import { authorDisplayName } from "@/lib/authors";
import { listBlogPosts } from "@/lib/site.functions";
import { ArrowRight } from "lucide-react";
import dealApprovalImage from "@/assets/salesforce-deal-approval-decision.jpg.asset.json";

// Slugs that are part of a multi-episode series and should only be reached
// from within the series flow (via the "Next in this series" CTA), not as
// separate cards in the insights index.
const HIDDEN_FROM_INDEX = new Set<string>([
  "hidden-value-in-your-salesforce-stack",
  "ai-procurement-trends-scandinavia-part-2",
]);

// Slug-scoped card image fallback for posts without a cover_image_url.
const CARD_IMAGE_BY_SLUG: Record<string, { src: string; alt: string }> = {
  "who-inside-salesforce-approves-your-deal": {
    src: dealApprovalImage.url,
    alt: "A businessman seen from behind, scratching his head, facing three arrows pointing in different directions and surrounded by question marks, illustrating the uncertainty of who makes the decision.",
  },
};

// ───────────────────────────────────────────────────────────────────────────
// FAQ content
// ───────────────────────────────────────────────────────────────────────────
type FaqItem = { q: string; a: string[]; callout?: string };
type FaqSection = { label: string; title: string; items: FaqItem[] };

export const FAQ_SECTIONS: FaqSection[] = [
  {
    label: "TIMING & LEVERAGE",
    title: "When and how to create the conditions for a better deal",
    items: [
      {
        q: "When is the best time to negotiate a Salesforce renewal?",
        a: [
          "Six months before renewal — minimum. If you wait until 90 days out, your leverage is structurally limited. Salesforce knows you won't migrate a CRM in three months, and they'll negotiate accordingly.",
          "The second variable is Salesforce's fiscal year, which runs February to January. The best moments to sign are at the end of their quarters: **end of April, July, October, or January**. Sales teams are under pressure to close, and that pressure translates into commercial flexibility you won't see mid-quarter.",
        ],
        callout:
          "One company we know tried to create leverage by cancelling their contract three months before renewal. Salesforce accepted the cancellation, waited, then reinstated at list price when the company returned. Don't do this. Real leverage takes months to build.",
      },
      {
        q: "How does Salesforce's fiscal year affect my negotiating position?",
        a: [
          "Salesforce's fiscal year ends in January. Q4 — November through January — is when the largest proportion of annual revenue closes. The pressure on AEs and their managers to hit quota is highest during this window, which gives buyers more room.",
          "That said, the effect is tiered by deal size. If you're spending millions annually, you will move the needle in every quarter — fiscal timing matters less. If your contract is under $500K per product, timing becomes more important because your deal only meaningfully affects the AE's numbers at peak pressure moments.",
          "End of month also matters, not just end of quarter. An AE who has committed a deal to their manager needs to close it. If your go-live date creates a genuine internal deadline, that can become a useful structural lever — or a liability, if Salesforce senses the urgency is real.",
        ],
      },
      {
        q: "We're not a huge company. Do we have any leverage at all?",
        a: [
          "Yes — but it depends on context, not just contract size. A useful benchmark: if your deal represents 50% or more of your AE's annual quota, you have their genuine attention. AEs in SMB segments carry quotas from around $600K. Enterprise AEs typically sit at $1.5M or more.",
          "If you're a small deal in an Enterprise segment, the AE may not be prioritising your renewal. In that case, the renewal team and customer success managers often care more than the AE — their metrics are tied to retention, not growth. That's worth understanding.",
          "The key is not to overstate leverage you don't have. Don't make threats you can't execute. Salesforce has seen every tactic and they know your actual position. What works is being well-prepared, knowing your usage data cold, and having a clear, consistent internal narrative.",
        ],
      },
    ],
  },
  {
    label: "DISCOUNTS & PRICING",
    title: "What's realistic, how approval works, and where the money actually comes from",
    items: [
      {
        q: "How much can you realistically discount Salesforce pricing?",
        a: [
          "There is no universal number — discount depth depends on deal size, product, region, timing, and whether this is new business or a renewal. What we can tell you is the structural reality: there are six approval levels inside Salesforce, and each level unlocks incrementally more room.",
          "Your AE (Level 0) has a limited, tightly managed band. Managers and directors (Levels 1–3) each hold a few percentage points. Deal Desk (Level 4) is where most negotiations actually stop — they are there specifically to protect Salesforce's pricing in the market, and they are unemotional. Level 5 (senior executives) comes into play primarily for very large deals or when a region is underperforming.",
          "The practical implication: to get meaningful discounts, you need to give L0–L3 the business justification to take to Deal Desk. That case — your usage data, your alternatives, your internal roadmap — is what gets deals approved. Pressure alone doesn't work at L4.",
        ],
        callout:
          "If your contract is below $500K per product, your negotiation band will be narrower. That doesn't mean there's nothing to do — it means the approach needs to be different. Speak with us if you want a candid view of your specific situation.",
      },
      {
        q: "What is Salesforce Deal Desk and why does it matter?",
        a: [
          "Deal Desk is the internal team inside Salesforce that approves any discount above what managers and directors can approve unilaterally. It exists to prevent AEs from over-discounting to hit their quota — and it is very effective at that job.",
          "Deal Desk does not have a relationship with you. They do not know your account, your history, or your business. Their job is to protect Salesforce's pricing position. Every request that comes to them needs a business justification — a reason why Salesforce should make a commercial exception in your case.",
          "This is where most companies lose ground without realising it. They focus on negotiating with their AE, who genuinely cannot give more than Level 0 allows. The AE then takes a poorly structured case to Deal Desk, gets rejected, and comes back to the customer empty-handed. The preparation you do months in advance — your usage audit, your competitive evaluation, your internal roadmap — is what gets Deal Desk to say yes.",
        ],
      },
      {
        q: "Should we sign a multi-year deal for a better discount?",
        a: [
          "Sometimes — but the decision is rarely as straightforward as Salesforce's AE will present it. Multi-year deals improve the unit price, which looks good in the initial negotiation. What they also do is remove your optionality for several years.",
          "The questions to ask before committing: Is your organisation actually using what you already have? Have you reached the adoption levels that justify the current stack, let alone an expanded one? A multi-year deal that locks in unused seats or unimplemented products creates sunk costs, not savings.",
          "We've seen multi-year deals sold on the back of Agentforce credits or other new products being bundled in early \"because it creates a better discount story over three years.\" If your team hasn't adopted what you already own, buying optimism over three years at a discount is still expensive.",
        ],
      },
    ],
  },
  {
    label: "PREPARATION & USAGE",
    title: "What to know about your own contract before you negotiate anything",
    items: [
      {
        q: "What should a CFO or CPO do before a Salesforce renewal?",
        a: [
          "**Own your usage data before Salesforce does.** Your AE is already building a picture of your account — active users, product engagement, which teams are using what. Make sure your picture is more accurate than theirs. That means understanding which licenses are active, which are sitting idle, and which users are on the wrong edition for what they actually do.",
          "**Get your internal story straight.** Salesforce operates across multiple levels of your organisation simultaneously — C-suite relationship management, technical conversations with architects and admins, commercial discussions with procurement. If your departments have different views on what you need, that incoherence gets used commercially. A single, aligned internal narrative is worth more than most negotiation tactics.",
          "**Surface friction early.** If something isn't working — poor adoption, integration problems, a product that hasn't delivered — raise it now, not at renewal. A genuine ongoing problem that's been visible for 12 months is real. A complaint raised 90 days before renewal looks like a tactic, and Salesforce will treat it as one.",
          "**Check your in-term add-ons.** Products added mid-contract almost always carry lower discounts than your original deal. Most organisations don't check these. You should, before the renewal quote lands.",
        ],
      },
      {
        q: "How do I know if we're overpaying for Salesforce?",
        a: [
          "Eight out of ten companies we speak to are overpaying — not always in list price, but in scope. They're paying for licenses that aren't used, editions that are over-provisioned, or products that were bought on a growth plan that hasn't materialised.",
          "The signals to look for: seats assigned to users who rarely log in; premium licenses on users who only need basic functionality; products that were bundled into the original deal but were never fully implemented; add-ons purchased mid-term that duplicated functionality already in the existing stack.",
          "Salesforce will not proactively tell you about underutilisation. That information is available to them, but surfacing it would reduce their renewal value. Understanding your own usage position is, in practice, the most reliable foundation for any commercial conversation.",
        ],
      },
      {
        q: "What is shelfware and how common is it?",
        a: [
          "Shelfware is software you're paying for but not using — licenses that are assigned, contracted, and invoiced, but generating no operational value. It is extremely common in Salesforce contracts, and it tends to accumulate at renewal when companies buy for a future state rather than their current one.",
          "A good example is Marketing Cloud Account Engagement (formerly Pardot). Most teams use it for basic email and a few nurture flows. But they're paying for a platform built for lead scoring, account-based marketing, and sales alignment. The features exist. The adoption doesn't. That gap is money.",
          "The fix is straightforward once you know what to look for: a usage audit maps active users, idle seats, and overprovisioned roles. In most cases, this surfaces restructuring opportunities even within existing contractual terms — and gives you a grounded, factual basis for the renewal conversation.",
        ],
      },
    ],
  },
  {
    label: "PROCESS & TACTICS",
    title: "How negotiations actually work and where companies lose ground",
    items: [
      {
        q: "What is Salesforce's \"divide and conquer\" strategy?",
        a: [
          "Salesforce is very good at multi-threading — managing separate conversations with different stakeholders inside your organisation simultaneously. Your CIO hears one thing. Your Salesforce admin hears another. Your procurement lead is getting different information. Your CFO is being engaged at an executive level.",
          "This isn't nefarious — it's how enterprise sales works. But it creates a real problem in negotiations: different parts of your team may be in different stages of their wish lists, with different tolerances for change. An internal stakeholder who wants a new product can, without intending to, become a commercial liability during renewal discussions.",
          "The counter is straightforward in principle: a single point of contact for all commercial communications, and a clear internal alignment on what you want from this negotiation and what you've agreed to leave for later. Salesforce doesn't need to know your future roadmap. Keep it internal until it's useful to surface.",
        ],
      },
      {
        q: "What is a SELA and should we pursue one?",
        a: [
          "SELA stands for Salesforce Enterprise License Agreement. The pitch is flexibility: instead of buying individual SKUs, you get broader access to a defined set of products, often with a consumption or user-based model.",
          "The appeal is real for some organisations. If you have genuine economies of scale, if your usage patterns are genuinely unpredictable, or if you're running a complex multi-product implementation — an enterprise agreement can make commercial sense.",
          "The risk is that the flexibility pitch often comes from the Salesforce account team and the internal champions who want access to more products. That's not the same as it making commercial sense for your organisation. Before pursuing one, ask: is there a genuine economy of scale that benefits us, or does this primarily benefit Salesforce's revenue growth on our account? Would pay-as-you-go with slightly more administration actually be better for how we buy?",
        ],
      },
      {
        q: "My AE says this is the best deal they can do. Is that true?",
        a: [
          "Sometimes. The honest answer is that at Level 0, AEs genuinely do exhaust their discretionary discount authority. The better, more experienced AEs hold their discounts tightly until you actually ask — they won't volunteer margin they don't have to give. Less experienced AEs often give more up front, which is its own problem.",
          "But when an AE says they've given everything they can, the question isn't whether they've exhausted Level 0 — it's whether you've given them a strong enough case to take to Levels 1–4. Deal Desk doesn't approve exceptions based on relationship or pressure. They approve them based on business justification: what is the commercial reason for Salesforce to accept a lower price here?",
          "If you haven't built that justification — through usage data, a credible internal roadmap, competitive context, timing — then the AE genuinely may not be able to move further. The constraint isn't their willingness. It's the absence of a case.",
        ],
      },
    ],
  },
  {
    label: "WORKING WITH SAASED",
    title: "What we do and when it makes sense to bring us in",
    items: [
      {
        q: "What does SaaSed actually do?",
        a: [
          "SaaSed is a specialist Salesforce procurement and commercial advisory company, focused entirely on Salesforce as a vendor. We work with mid-to-large enterprises — typically companies spending between $1M and $10M annually on Salesforce — who want to know they're getting a fair deal and don't want the commercial complexity to land on their internal team.",
          "In practice, we do three things. We audit your current contract and usage so you know your actual position before any negotiation starts. We advise on or directly lead the negotiation process, either visibly or as a silent partner behind your team. And we help you build the internal alignment and documentation that turns a vague dissatisfaction into a negotiable position.",
          "We only take on engagements where we believe we can make a material difference. Our capacity at any given time is limited to six concurrent negotiations. That's a deliberate constraint — not a growth target.",
        ],
      },
      {
        q: "When in the renewal cycle should we bring you in?",
        a: [
          "Ideally 12 to 18 months before the contract end date. Starting 8 to 12 months ahead is still a good window, but the leverage you have shrinks with every month after that. The most effective work happens before formal negotiation starts — in the period where you're building leverage, surfacing friction with your AE, and aligning internally. By the time a renewal quote lands, the conditions for a good deal have usually already been set or missed.",
          "That said, if you're already inside the 90-day window and you haven't started: call us anyway. There's still work to do. The options are narrower, but they exist. What doesn't work is starting the day before the contract expires.",
          "However, mid-contract additions change the picture significantly. If you need to expand — adding Agentforce in year two of a three-year contract, for example — your AE will almost always position this as an early renewal opportunity. It is not. It is a new commercial event, and it carries the same risks as any negotiation: an incomplete picture of your usage, an unvalidated product mix, and no benchmark for what a fair price looks like in your current position. Coming in without that preparation is how mid-contract expansions end up costing more than they should, and locking in terms you'll be managing for longer than you planned.",
        ],
        callout:
          "The most common thing we hear: \"We wish we'd called you three months earlier.\" We can work with compressed timelines — but the outcomes are consistently better when there's time to build the case properly.",
      },
      {
        q: "Will Salesforce know we're using an outside advisor?",
        a: [
          "In most cases, it is more effective to have us visibly at the table. It signals immediately that you are approaching this seriously, that you have specialist expertise in your corner, and that the commercial process will be structured and well-prepared. Salesforce knows what that means. It changes the dynamic before the first conversation starts.",
          "In other cases — where internal politics, existing relationships, or the nature of the engagement make a lower profile more appropriate — we work behind your team. We advise on strategy, prepare the commercial case, review offers, and brief your negotiators before each stage. The outcome is the same; our presence just isn't the one in the room.",
          "We will listen and advise on the right approach for your situation.",
        ],
      },
    ],
  },
];

// Lightweight inline-bold renderer: turns **text** into <strong>text</strong>.
function renderBold(text: string) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    const m = part.match(/^\*\*([^*]+)\*\*$/);
    if (m) return <strong key={i} className="font-semibold text-foreground">{m[1]}</strong>;
    return <span key={i}>{part}</span>;
  });
}

// Layout route only: no head metadata here, so child article routes own their
// own canonical/meta tags without a duplicate /blog canonical being emitted.
export const Route = createFileRoute("/blog")({
  component: () => <Outlet />,
});

export function BlogIndex() {
  const location = useLocation();
  // Posts are loaded server-side by the /blog/ route loader so every
  // article link is present in the raw HTML.
  const data = getRouteApi("/blog/").useLoaderData() as Awaited<ReturnType<typeof listBlogPosts>>;
  const isLoading = false;
  const [showMore, setShowMore] = useState(false);

  if (location.pathname.replace(/\/$/, "") !== "/blog") {
    return <Outlet />;
  }

  const visiblePosts = (data?.posts ?? [])
    .filter((p) => !HIDDEN_FROM_INDEX.has(p.slug))
    .filter((p) => !/\bpart\s*(?:[2-9]|\d{2,})\b/i.test(p.title))
    .sort((a, b) => new Date(b.published_at).getTime() - new Date(a.published_at).getTime());

  const featured = visiblePosts.slice(0, 3);
  const rest = visiblePosts.slice(3);

  return (
    <>
      <section className="container-x py-20 md:py-28">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Insights</p>
        <h1 className="mt-3 max-w-3xl font-sans text-5xl leading-[1.05] md:text-6xl font-semibold">
          Market intelligence for Salesforce buyers.
        </h1>
        <p className="mt-6 max-w-2xl text-lg text-muted-foreground">
          Benchmarks, playbooks, and field notes from inside live negotiations across EMEA.
        </p>

        <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {isLoading &&
            Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-56 animate-pulse rounded-2xl bg-secondary/50" />
            ))}

          {!isLoading && featured.map((post) => <PostCard key={post.slug} post={post} />)}
        </div>

        {!isLoading && rest.length > 0 && (
          <>
            {/* Lightweight, always-present link list so crawlers see every
                article in the server HTML. Hidden via CSS only. */}
            <ul hidden aria-hidden="true">
              {rest.map((post) => (
                <li key={post.slug}>
                  <a href={`/blog/${post.slug}`} tabIndex={-1}>
                    {post.title}
                  </a>{" "}
                  <time dateTime={post.published_at.slice(0, 10)}>{post.published_at.slice(0, 10)}</time>
                </li>
              ))}
            </ul>
            {showMore && (
              <div className="mt-6 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {rest.map((post) => (
                  <PostCard key={post.slug} post={post} />
                ))}
              </div>
            )}

            <div className="mt-10 flex justify-center">
              <button
                type="button"
                onClick={() => setShowMore((v) => !v)}
                className="font-sans text-sm font-medium uppercase tracking-[0.15em] rounded-full border border-border bg-card px-6 py-3 text-foreground transition-colors hover:bg-secondary"
              >
                {showMore ? "Show fewer articles" : `Show more articles (${rest.length})`}
              </button>
            </div>
          </>
        )}

        {!isLoading && data?.posts.length === 0 && (
          <p className="text-muted-foreground">No posts yet.</p>
        )}
      </section>

      {/* ── FAQ section ── */}
      <section className="bg-brand-primary text-brand-ink">
        <div className="container-x py-24 md:py-28">
          {/* Editorial header */}
          <div className="grid gap-10 md:grid-cols-12 md:gap-12 items-end border-b border-brand-ink/15 pb-10 md:pb-14">
            <div className="md:col-span-7">
              <div className="flex items-center gap-3 text-[11px] font-body font-semibold uppercase tracking-[0.22em] text-brand-ink/70">
                <span className="h-px w-8 bg-brand-ink/40" />
                <span>FAQ · Vol. 01</span>
              </div>
              <h2 className="mt-6 font-display font-medium text-[clamp(3.25rem,8.5vw,7.5rem)] leading-[0.92] tracking-[-0.035em] text-brand-ink">
                The questions
                <br />
                CFOs &amp; CIOs
                <br />
                <span className="italic text-brand-ink underline decoration-[#f5cc00] decoration-2 underline-offset-4">actually</span> ask.
              </h2>
            </div>
            <div className="md:col-span-5 md:pb-4">
              <p className="font-body text-base md:text-lg leading-relaxed text-brand-ink/75 max-w-md">
                No jargon. No sales pitch. Just what you need to know before your next Salesforce renewal — written by people who&apos;ve spent years on both sides of the table.
              </p>
            </div>
          </div>

          {/* Sections */}
          <div className="mt-16 md:mt-24 space-y-20 md:space-y-28">
            {FAQ_SECTIONS.map((section, sIndex) => (
              <div key={section.label} className="grid gap-8 md:grid-cols-12 md:gap-12">
                <div className="md:col-span-4">
                  <div className="sticky top-24">
                    <div className="font-body text-[11px] font-semibold uppercase tracking-[0.22em] text-brand-ink">
                      {String.fromCharCode(65 + sIndex)} — {section.label.toUpperCase()}
                    </div>
                    <h3 className="mt-4 font-display font-medium text-3xl md:text-4xl leading-[1.05] tracking-[-0.02em] text-brand-ink">
                      {section.title}
                    </h3>
                  </div>
                </div>

                <div className="md:col-span-8">
                  <FaqAccordion section={section} />
                </div>
              </div>
            ))}
          </div>

          <nav aria-label="Related" className="mt-12 flex flex-col items-center gap-4 text-center md:mt-16">
            <p className="font-sans text-[11px] font-medium uppercase tracking-[0.12em] text-muted-foreground">RELATED</p>
            <div className="flex flex-wrap justify-center gap-x-6 gap-y-4 font-sans">
              <Link to="/salesforce-procurement" className="underline! decoration-[#f5cc00]! decoration-2! underline-offset-4 hover:text-[#1a1408]">Salesforce procurement advisory</Link>
              <Link to="/services" className="underline! decoration-[#f5cc00]! decoration-2! underline-offset-4 hover:text-[#1a1408]">Advisory services</Link>
              <Link to="/dioro" className="underline! decoration-[#f5cc00]! decoration-2! underline-offset-4 hover:text-[#1a1408]">Dioro</Link>
              <Link to="/case-study" className="underline! decoration-[#f5cc00]! decoration-2! underline-offset-4 hover:text-[#1a1408]">PHMG case study</Link>
            </div>
          </nav>

          {/* CTA block */}
          <div className="mt-24 md:mt-32 bg-brand-ink text-brand-cream p-10 md:p-14 flex flex-col md:flex-row md:items-center md:justify-between gap-8 rounded-sm">
            <div className="max-w-2xl">
              <div className="font-body text-[11px] font-semibold uppercase tracking-[0.22em] text-brand-primary">
                Complimentary audit
              </div>
              <h3 className="mt-4 font-display font-medium text-3xl md:text-5xl leading-[1.05] tracking-[-0.02em]">
                Not sure where you stand?
              </h3>
              <p className="mt-4 font-body text-base md:text-lg text-brand-cream/70 max-w-xl leading-relaxed">
                No obligation — just a clear view of your current position before your next renewal.
              </p>
            </div>
            <Link
              to="/contact"
              className="font-body text-sm font-bold uppercase tracking-[0.15em] bg-brand-primary px-7 py-4 whitespace-nowrap hover:bg-brand-punch transition-colors rounded-sm"
            >
              <span className="!text-black" style={{ color: "#000000 !important" }}>
                REQUEST A COMPLIMENTARY AUDIT
              </span>
            </Link>
          </div>
        </div>
      </section>

    </>
  );
}

// Used only by BlogIndex, whose pathname guard excludes article/author pages.
// Native details keep every answer in server HTML, with all rows initially closed.
function FaqAccordion({ section }: { section: FaqSection }) {
  return (
    <div className="mx-auto mt-12 flex max-w-3xl flex-col gap-4">
      {section.items.map((item, i) => (
        <details key={item.q} className="group overflow-hidden rounded-2xl border border-[#f0e8c8] bg-[#fffdf0] shadow-[0_1px_2px_rgba(26,20,8,0.06)] transition-colors duration-150 hover:border-[#1a1408]/30">
          <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-6 py-5 md:px-8 md:py-6 rounded-2xl focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1a1408] [&::-webkit-details-marker]:hidden">
            <span aria-hidden="true" className="font-sans text-[11px] font-medium uppercase tracking-[0.12em] text-muted-foreground">
              {String(i + 1).padStart(2, "0")}
            </span>
            <h3 className="m-0 font-sans text-lg font-medium leading-[1.35] tracking-normal text-[#1a1408] md:text-xl">{item.q}</h3>
            <span aria-hidden="true" className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#f0e8c8] transition-colors duration-200 group-open:bg-[#f5cc00]">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="transition-transform duration-200 group-open:rotate-45"><path d="M7 1v12M1 7h12" stroke="#1a1408" strokeWidth="2" strokeLinecap="round" /></svg>
            </span>
          </summary>
          <div className="flex flex-col gap-4 border-t border-[#f0e8c8] px-6 pb-6 pt-5 md:px-8 md:pb-8">
            {item.a.map((p, j) => (
              <p key={j} className="m-0 text-left text-base leading-[1.7] text-[#1a1408]/85">{renderBold(p)}</p>
            ))}
            {item.callout && (
              <p className="m-0 text-left text-base leading-[1.7] text-[#1a1408]/85">{renderBold(item.callout)}</p>
            )}
          </div>
        </details>
      ))}
    </div>
  );
}

function PostCard({ post }: { post: { slug: string; title: string; excerpt: string; category: string; author: string; cover_image_url: string | null; published_at: string } }) {
  const cardFallback = CARD_IMAGE_BY_SLUG[post.slug];
  const cardImage = post.cover_image_url ?? cardFallback?.src;
  const cardAlt = cardFallback?.alt ?? post.title;
  return (
    <Link
      to="/blog/$slug"
      params={{ slug: post.slug }}
      className="group flex flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-soft transition-all hover:-translate-y-1 hover:shadow-glow"
    >
      {cardImage && (
        <div className="aspect-[16/9] w-full overflow-hidden bg-secondary">
          <img
            src={cardImage}
            alt={cardAlt}
            loading="lazy"
            width={1280}
            height={768}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
          />
        </div>
      )}
      <div className="flex flex-1 flex-col p-8">
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <span className="rounded-full bg-primary/40 px-2.5 py-0.5 font-medium text-foreground">
            {post.category}
          </span>
          <span>
            {new Date(post.published_at).toLocaleDateString("en-GB", {
              day: "numeric",
              month: "short",
              year: "numeric",
            })}
          </span>
        </div>
        <h2 className="mt-4 font-sans font-semibold text-2xl leading-snug md:text-3xl">{post.title}</h2>
        <p className="mt-3 text-sm text-muted-foreground line-clamp-3">{post.excerpt}</p>
        <div className="mt-6 flex items-center justify-between text-sm">
          <span className="text-muted-foreground">{authorDisplayName(post.author)}</span>
          <span className="inline-flex items-center gap-1 font-medium text-foreground">
            Read <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
          </span>
        </div>
      </div>
    </Link>
  );
}
